// frontend/src/features/citysense/housing-stock-modeller/components/SummaryTable.tsx

import React from "react";
import { HousingSummary } from "../types";
import { Building2, Home, KeyRound } from "lucide-react";

interface Props {
  summary: HousingSummary;
  archetypeColors: Map<string, string>;
}

// frontend/src/features/citysense/housing-stock-modeller/components/SummaryTable.tsx

export const SummaryTable: React.FC<Props> = ({ summary, archetypeColors }) => {
  return (
    <div className="bg-white border-2 border-teal-200 rounded-xl shadow-lg p-6">
      <h3 className="text-lg font-bold text-gray-800 mb-4">Region Summary</h3>

      {/* Key Metrics */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <MetricCard
          icon={<Building2 className="w-6 h-6 text-teal-600" />}
          label="Total Dwellings"
          value={summary.totalDwellings.toLocaleString()}
        />
        <MetricCard
          icon={<Home className="w-6 h-6 text-blue-600" />}
          label="Houses"
          value={summary.totalHouses.toLocaleString()}
        />
        <MetricCard
          icon={<KeyRound className="w-6 h-6 text-purple-600" />}
          label="Flats"
          value={summary.totalFlats.toLocaleString()}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <MetricCard
          icon={<span className="text-2xl">💰</span>}
          label="Total Retrofit Cost"
          value={`€${(summary.totalRetrofitCost / 1000000000).toFixed(2)}B`}
          subtext="full upgrade"
        />
        <MetricCard
          icon={<span className="text-2xl">🌱</span>}
          label="GHG Savings (20yr)"
          value={`${(summary.totalGHGSavings / 1000).toFixed(1)}k tCO₂e`}
          subtext="potential reduction"
        />
      </div>

      {/* Archetype Breakdown */}
      <h4 className="text-sm font-bold text-gray-700 mb-3 border-b-2 border-gray-200 pb-2">
        Archetype Breakdown
      </h4>
      <div className="space-y-2 max-h-96 overflow-y-auto pr-2">
        {summary.archetypeBreakdown
          .sort((a, b) => b.dwellings - a.dwellings)
          .map((row) => {
            // Fix broken diamond characters (◆ showing as �)
            const cleanName = row.archetype.replace(/�/g, "◆");

            return (
              <div
                key={row.archetype}
                className="flex items-start justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors gap-3"
              >
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div
                    className="w-6 h-6 rounded border-2 border-white shadow-sm flex-shrink-0 mt-0.5"
                    style={{
                      backgroundColor:
                        archetypeColors.get(row.archetype) || "#ccc",
                    }}
                  />
                  <span className="text-xs font-semibold text-gray-700 leading-tight break-words">
                    {cleanName}
                  </span>
                </div>
                <div className="flex gap-4 text-xs text-gray-600 flex-shrink-0">
                  <div className="text-right">
                    <div className="font-bold text-gray-800">
                      {row.dwellings.toLocaleString()}
                    </div>
                    <div className="text-xs whitespace-nowrap">dwellings</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-800">{row.count}</div>
                    <div className="text-xs whitespace-nowrap">polygons</div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
};

const MetricCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
}> = ({ icon, label, value, subtext }) => (
  <div className="bg-gradient-to-br from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-lg p-4 hover:shadow-md transition-shadow">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs font-semibold text-gray-600">{label}</span>
    </div>
    <div className="text-xl font-bold text-gray-800">{value}</div>
    {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
  </div>
);
 